import { cn } from '@/lib/utils'

/**
 * The ITPU logo — the official vector wordmark from itpu.uz, paths inlined so
 * the fills follow the theme. The mark reads "iTPU": the letters plus a
 * detached round dot over the i, which is the only part that carries the brand
 * teal in the colour tone.
 *
 * Variants follow the brand guideline's usage rules:
 *   horizontal — the wordmark alone
 *   full       — wordmark + the "IT PARK UNIVERSITY" lockup behind a hairline
 *   round      — wordmark knocked out of a solid teal circle
 */

const VIEWBOX_W = 106
const VIEWBOX_H = 52

const PATH_ALL =
  'M17.0601 10.5706C17.0601 8.04611 15.0309 6 12.5281 6C10.0292 6 8 8.04611 8 10.5706C8 11.9308 8.58912 13.1527 9.52477 13.9904C10.3257 14.7051 11.3768 15.1393 12.5281 15.1393C15.0309 15.1393 17.0601 13.0932 17.0601 10.5706ZM16.4979 18.8818H9.04347V45.4697H16.4979V18.8818ZM19.3203 24.8453H27.4139V45.4697H34.8683V24.8453H42.9234V18.8818H19.3203V24.8453ZM63.9699 20.0788C62.2102 19.2815 60.1426 18.8818 57.7553 18.8818H45.7111V45.4697H53.1656V38.5187H57.7553C59.0683 38.5187 60.2889 38.3977 61.4132 38.1556C62.3296 37.9577 63.1805 37.6792 63.9699 37.3218C65.7257 36.5245 67.081 35.391 68.0359 33.9231C68.987 32.4534 69.4644 30.7205 69.4644 28.7205C69.4644 26.7185 68.987 24.9779 68.0359 23.4966C67.081 22.0154 65.7257 20.8761 63.9699 20.0788ZM60.7663 31.5869C59.9885 32.2574 58.8373 32.5937 57.3048 32.5937H53.1656V24.8069H57.3048C58.8373 24.8069 59.9885 25.1489 60.7663 25.8329C61.548 26.5168 61.9369 27.4793 61.9369 28.7205C61.9369 29.9597 61.548 30.9164 60.7663 31.5869ZM76.4838 42.7723C78.6554 44.9241 81.7011 46 85.6132 46C89.5291 46 92.5747 44.9241 94.7425 42.7723C96.9142 40.6206 98 37.5677 98 33.6196V18.8818H90.6611V33.391C90.6611 37.6446 89.0015 39.7714 85.6902 39.7714C82.3518 39.7714 80.6846 37.6446 80.6846 33.391V18.8818H73.2302V33.6196C73.2302 37.5677 74.316 40.6206 76.4838 42.7723Z'

const PATH_DOT =
  'M17.0601 10.5706C17.0601 8.04611 15.0309 6 12.5281 6C10.0292 6 8 8.04611 8 10.5706C8 11.9308 8.58912 13.1527 9.52477 13.9904C10.3257 14.7051 11.3768 15.1393 12.5281 15.1393C15.0309 15.1393 17.0601 13.0932 17.0601 10.5706Z'

export type LogoTone = 'color' | 'dark' | 'white' | 'mono'
export type LogoVariant = 'horizontal' | 'full' | 'round'

const TONES: Record<LogoTone, { letter: string; dot: string }> = {
  // `--text` rather than `--itpu-ink`: the two are the same colour in the light
  // theme, but `--itpu-ink` is a fixed brand swatch and does not flip for dark,
  // where it left the wordmark as dark grey on a dark ground — invisible. The
  // dot keeps the brand teal, which reads on both.
  color: { letter: 'rgb(var(--text))', dot: 'rgb(var(--itpu-sharp-blue))' },
  // Deliberately the fixed swatch: this tone exists for the cases that are
  // always on a light ground, print among them.
  dark: { letter: 'rgb(var(--itpu-ink))', dot: 'rgb(var(--itpu-ink))' },
  white: { letter: 'rgb(var(--itpu-white))', dot: 'rgb(var(--itpu-white))' },
  mono: { letter: 'currentColor', dot: 'currentColor' },
}

function Wordmark({ height, letter, dot }: { height: number; letter: string; dot: string }) {
  return (
    <svg
      width={(height * VIEWBOX_W) / VIEWBOX_H}
      height={height}
      viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
      fill="none"
      role="img"
      aria-label="ITPU"
      className="block"
    >
      <path fillRule="evenodd" clipRule="evenodd" d={PATH_ALL} fill={letter} />
      <path d={PATH_DOT} fill={dot} />
    </svg>
  )
}

export function Logo({
  variant = 'horizontal',
  tone = 'color',
  size = 28,
  className,
}: {
  variant?: LogoVariant
  tone?: LogoTone
  size?: number
  className?: string
}) {
  const fills = TONES[tone]

  if (variant === 'round') {
    return (
      <span
        role="img"
        aria-label="ITPU"
        className={cn('inline-flex items-center justify-center rounded-pill', className)}
        style={{
          width: size * 2.6,
          height: size * 2.6,
          background: tone === 'white' ? 'transparent' : 'rgb(var(--itpu-sharp-blue))',
          border: tone === 'white' ? '2px solid rgb(var(--itpu-white))' : 'none',
        }}
      >
        <Wordmark height={size} letter="rgb(var(--itpu-white))" dot="rgb(var(--itpu-white))" />
      </span>
    )
  }

  if (variant === 'full') {
    return (
      <span className={cn('inline-flex items-center', className)} style={{ gap: size * 0.34 }}>
        <Wordmark height={size} letter={fills.letter} dot={fills.dot} />
        <span
          aria-hidden
          className="self-stretch"
          style={{
            width: 2,
            margin: `${size * 0.08}px 0`,
            background: tone === 'white' ? 'rgb(var(--itpu-white) / 0.35)' : 'rgb(var(--border))',
          }}
        />
        <span
          className="font-semibold uppercase leading-[1.08] tracking-[0.01em]"
          style={{
            fontSize: size * 0.34,
            color: tone === 'white' ? 'rgb(var(--itpu-white))' : 'rgb(var(--text))',
          }}
        >
          IT Park
          <br />
          University
        </span>
      </span>
    )
  }

  return (
    <span className={cn('inline-flex', className)}>
      <Wordmark height={size} letter={fills.letter} dot={fills.dot} />
    </span>
  )
}
